/**
 * A filtered, sorted, paged list of one entity type — global or nested.
 *
 * This is the component the whole port existed for. A municipality page
 * mounts two of them (its procurements and its companies) and each keeps
 * its own filters, page and sort in the same URL, separated by the prefix
 * on its facet set. Before this, detail pages fetched every related row
 * inline: Sofia's page pulled 489 procurements in a 204 KB response, with
 * no limit, on a graph holding a fraction of the eventual archive.
 *
 * Counts and rows are fetched as one query pair so the sidebar always
 * describes the set the table is showing.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { request } from '../lib/graphql';
import { useScopedParams } from '../lib/router';
import type { FacetCounts, FacetSet } from '../facets/types';
import { DataTable, Pager, type Column, type SortState } from './DataTable';
import { FacetSidebar } from './FacetSidebar';
import { ErrorState, Loading, Refreshing, SectionHead } from './ui';
import { downloadXlsx, sheetOf, stampedFilename, type ExportColumn } from '../lib/xlsx';

const PAGE_SIZE = 25;

/* The export re-runs the list query page by page rather than asking for
   everything at once: the same request shape the server already answers,
   just walked to the end. Capped, because "export" must not become the
   one button that asks the database for the entire archive. */
const EXPORT_PAGE = 200;
const EXPORT_CAP = 10_000;

interface FacetGroupResult {
  key: string;
  values: readonly { value: string; count: number }[];
}

/*
 * Variables are `any` on purpose: codegen types them as Exact<...>, which
 * is invariant, so a document with concrete variables is not assignable to
 * one declared with a wider variable type. The variables here are built by
 * this component and are the same for every list. What matters is that the
 * *data* type flows through, which is what selectRows is checked against —
 * so a query that stops selecting a field fails to compile at the call
 * site rather than returning undefined at runtime.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyVariables = any;

export interface FacetedListProps<
  TState extends Record<string, unknown>, TRow, TListData, TFacetData,
> {
  facets: FacetSet<TState, Record<string, unknown>, Record<string, unknown>>;
  /** Paged rows plus a total, both taking the same `where`. */
  listDocument: TypedDocumentNode<TListData, AnyVariables>;
  /** Facet counts, taking the structured filter. Omit for a list with no
      count query — passing the rows document instead fires a request
      missing its limit/offset and fails on every load. */
  facetsDocument?: TypedDocumentNode<TFacetData, AnyVariables> | undefined;
  /** Pulls rows and total out of the list response. */
  selectRows: (data: TListData) => { rows: TRow[]; total: number };
  /** Pulls the facet groups out of the facets response. */
  selectFacets: (data: TFacetData) => readonly FacetGroupResult[];
  columns: readonly Column<TRow>[];
  rowKey: (row: TRow, index: number) => string;
  defaultSort: SortState;
  searchPlaceholder: string;
  /** Rendered above the list when it sits inside a dossier. */
  heading?: string;
  headingAside?: string;
  emptyMessage?: string;
  /** Offers the filtered set as an .xlsx download. `name` is the sheet and
      file name («фирми»); the columns read raw values — identifiers whole,
      amounts as numbers — rather than the truncated display strings. */
  exportable?: { name: string; columns: readonly ExportColumn<TRow>[] };
}

export function FacetedList<
  TState extends Record<string, unknown>, TRow, TListData, TFacetData,
>({
  facets, listDocument, facetsDocument, selectRows, selectFacets, columns, rowKey,
  defaultSort, searchPlaceholder, heading, headingAside, emptyMessage, exportable,
}: FacetedListProps<TState, TRow, TListData, TFacetData>) {
  const params = useScopedParams(facets.prefix);

  const state = useMemo(
    () => facets.fromParams(params.get, params.getAll),
    [facets, params],
  );
  const page = Math.max(0, (Number(params.get('page')) || 1) - 1);
  const sort: SortState = {
    key: params.get('sort') ?? defaultSort.key,
    dir: params.get('dir') === 'ASC' ? 'ASC' : (params.get('dir') as 'DESC' | null) ?? defaultSort.dir,
  };

  /* The search box keeps its own value so typing is not throttled by the
     round trip; the committed term lives in the URL. */
  const [draft, setDraft] = useState<string>(() => (state['search'] as string) ?? '');

  /* On a phone the facet panel is a bottom sheet behind a „Филтри“ button;
     it stays open while boxes are ticked so the counts can be watched
     narrowing. On wide screens the same panel is the always-visible
     sidebar and this state has no effect. */
  const [filtersOpen, setFiltersOpen] = useState(false);
  useEffect(() => {
    if (!filtersOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFiltersOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filtersOpen]);

  const where = useMemo(() => facets.toWhere(state), [facets, state]);
  const filter = useMemo(() => facets.toFilter(state), [facets, state]);

  const listQuery = useQuery({
    queryKey: [facets.prefix, 'rows', where, sort, page],
    queryFn: () => request(listDocument, {
      limit: PAGE_SIZE, offset: page * PAGE_SIZE, where,
      sort: [{ [sort.key]: sort.dir }],
    }),
    // keeps the previous page visible while the next loads, so paging does
    // not flash the table away
    placeholderData: keepPreviousData,
  });

  const facetQuery = useQuery({
    queryKey: [facets.prefix, 'facets', filter],
    queryFn: () => request(facetsDocument!, { filter }),
    enabled: Boolean(facetsDocument),
    placeholderData: keepPreviousData,
  });

  const counts: FacetCounts = useMemo(() => {
    if (!facetQuery.data) return {};
    return Object.fromEntries(selectFacets(facetQuery.data).map((group) => [
      group.key, Object.fromEntries(group.values.map((entry) => [entry.value, entry.count])),
    ]));
  }, [facetQuery.data, selectFacets]);

  const apply = useCallback((next: TState, resetPage = true) => {
    params.set({
      ...facets.toParams(next),
      ...(resetPage ? { page: null } : {}),
    });
  }, [facets, params]);

  const onToggleList = useCallback((key: string, value: string) => {
    const current = state[key];
    const list = Array.isArray(current) ? (current as string[]) : [];
    const nextList = list.includes(value)
      ? list.filter((entry) => entry !== value)
      : [...list, value];
    apply({ ...state, [key]: nextList });
  }, [apply, state]);

  const onSetValue = useCallback((key: string, value: string | boolean) => {
    apply({ ...state, [key]: value });
  }, [apply, state]);

  const onReset = useCallback(() => {
    setDraft('');
    params.clear();
  }, [params]);

  const commitSearch = useCallback((term: string) => {
    apply({ ...state, search: term });
  }, [apply, state]);

  /* Rows fetched so far, or null when no export is running — the button
     doubles as the progress display. */
  const [exporting, setExporting] = useState<number | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const exportAll = useCallback(async () => {
    if (!exportable) return;
    setExportError(null);
    setExporting(0);
    try {
      const rows: TRow[] = [];
      let total = 0;
      for (let offset = 0; ; offset += EXPORT_PAGE) {
        const data = await request(listDocument, {
          limit: EXPORT_PAGE, offset, where,
          sort: [{ [sort.key]: sort.dir }],
        });
        const page = selectRows(data);
        total = page.total;
        rows.push(...page.rows);
        setExporting(rows.length);
        if (page.rows.length < EXPORT_PAGE || rows.length >= Math.min(total, EXPORT_CAP)) break;
      }
      const kept = rows.slice(0, EXPORT_CAP);

      /* What was asked for, alongside what came back — a spreadsheet gets
         forwarded, and a forwarded table with no provenance turns into
         "data from the internet" within a week. */
      const filters = Object.entries(facets.toParams(state))
        .filter(([, value]) => value != null && String(value) !== '')
        .map(([key, value]) => [key, Array.isArray(value) ? value.join(', ') : String(value)]);
      const provenance: (string | number)[][] = [
        ['Източник', 'Алфа-Омега — от бюджета до получателя'],
        ['Изтеглено на', new Date().toISOString()],
        ['Редове', kept.length],
        ['Общо в извадката', total],
        ['Подредба', `${sort.key} ${sort.dir}`],
        ...(filters.length ? filters : [['Филтри', 'няма — пълният регистър']]),
        ...(total > EXPORT_CAP
          ? [['Внимание', `изнесени са първите ${EXPORT_CAP} реда; стеснете филтрите за останалите`]]
          : []),
      ];

      downloadXlsx(stampedFilename(exportable.name), [
        sheetOf(exportable.name, exportable.columns, kept),
        { name: 'Справка', header: ['Поле', 'Стойност'], rows: provenance },
      ]);
    } catch (error) {
      setExportError((error as Error).message);
    } finally {
      setExporting(null);
    }
  }, [exportable, facets, listDocument, selectRows, sort.dir, sort.key, state, where]);

  const { rows, total } = listQuery.data
    ? selectRows(listQuery.data)
    : { rows: [] as TRow[], total: 0 };

  const body = (() => {
    if (listQuery.isError) {
      return <ErrorState message={(listQuery.error).message} />;
    }
    if (listQuery.isPending) return <Loading rows={8} />;
    return (
      // The rows on screen are the previous answer while the next one is
      // fetched — that is what keepPreviousData buys, and unmarked it
      // reads as a filter that did nothing.
      <Refreshing busy={listQuery.isFetching}>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={rowKey}
          sort={sort}
          onSort={(key) => params.set({
            sort: key,
            dir: sort.key === key && sort.dir === 'DESC' ? 'ASC' : 'DESC',
            page: null,
          })}
          {...(emptyMessage ? { empty: emptyMessage } : {})}
        />
        <Pager page={page} pageSize={PAGE_SIZE} total={total}
          onGo={(next) => params.set({ page: next ? String(next + 1) : null })} />
      </Refreshing>
    );
  })();

  const hasFacets = facets.groups.length > 0;
  const activeFilters = hasFacets ? facets.activeCount(state) : 0;
  /* the header row that carries sorting on wide screens is hidden on a
     phone, so the same sort choices are offered as a select */
  const sortableColumns = columns.filter((column) => column.sortable && column.label);

  return (
    <section className={heading ? 'block' : undefined}>
      {heading && <SectionHead title={heading} aside={headingAside} />}
      {/* without facets there is no sidebar to make room for */}
      <div className={hasFacets ? 'list-shell' : undefined}>
        {hasFacets && (
          <div className={filtersOpen ? 'facet-drawer is-open' : 'facet-drawer'}>
            <div
              className="facet-scrim"
              aria-hidden="true"
              onClick={() => setFiltersOpen(false)}
            />
            <FacetSidebar
              facets={facets}
              state={state}
              counts={counts}
              onToggleList={onToggleList}
              onSetValue={onSetValue}
              onReset={onReset}
              onClose={() => setFiltersOpen(false)}
            />
          </div>
        )}
        <div>
          <div className="toolbar">
            <input
              type="search"
              placeholder={searchPlaceholder}
              enterKeyHint="search"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') commitSearch(draft);
              }}
              onBlur={() => {
                if (draft !== ((state['search'] as string) ?? '')) commitSearch(draft);
              }}
            />
            <button type="button" className="btn" onClick={() => commitSearch(draft)}>
              Търси
            </button>
            {hasFacets && (
              <button
                type="button"
                className="btn facet-toggle"
                aria-expanded={filtersOpen}
                onClick={() => setFiltersOpen(true)}
              >
                Филтри{activeFilters > 0 ? ` · ${activeFilters}` : ''}
              </button>
            )}
            {sortableColumns.length > 0 && (
              <label className="sort-mobile">
                <span className="sr-only">Подредба</span>
                <select
                  value={`${sort.key}:${sort.dir}`}
                  onChange={(event) => {
                    const [key, dir] = event.target.value.split(':');
                    params.set({ sort: key ?? null, dir: dir ?? null, page: null });
                  }}
                >
                  {sortableColumns.flatMap((column) => [
                    <option key={`${column.key}:DESC`} value={`${column.key}:DESC`}>
                      {column.label} ↓
                    </option>,
                    <option key={`${column.key}:ASC`} value={`${column.key}:ASC`}>
                      {column.label} ↑
                    </option>,
                  ])}
                </select>
              </label>
            )}
            {exportable && (
              /* Exports the whole filtered set, not the visible page — the
                 pager is for reading, the file is for working. */
              <button
                type="button"
                className="btn"
                disabled={exporting != null}
                onClick={() => { void exportAll(); }}
                title="Изтегля целия филтриран списък като файл за Excel"
              >
                {exporting != null
                  ? `Изтегляне… ${exporting.toLocaleString('bg-BG')}`
                  : 'Експорт XLSX'}
              </button>
            )}
          </div>
          {exportError && (
            <p className="colophon-note" role="alert">
              Експортът не сполучи: {exportError}
            </p>
          )}
          {body}
        </div>
      </div>
    </section>
  );
}
